import type { DragEndEvent } from '@dnd-kit/core';
import {
  DndContext,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  SortableContext,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import type { CollapseProps } from 'antd';
import { Collapse } from 'antd';
import classNames from 'classnames';
import type { FC, ReactNode } from 'react';
import React from 'react';

type SortableCollapseProps = CollapseProps & {
  activeKey: string[];
  onToggle: (key: string[]) => void;
  children: ReactNode;
  className?: string;
  items: (string | number)[];
  onSortEnd: (indexes: { oldIndex: number; newIndex: number }) => void;
};

export const SortableCollapse: FC<SortableCollapseProps> = ({
  activeKey,
  onToggle,
  children,
  className,
  items,
  onSortEnd,
}) => {
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } })
  );

  const handleDragEnd = ({ active, over }: DragEndEvent) => {
    if (!over || active.id === over.id) {
      return;
    }

    const oldIndex = items.findIndex((id) => id === active.id);
    const newIndex = items.findIndex((id) => id === over.id);

    if (oldIndex !== -1 && newIndex !== -1) {
      onSortEnd({ oldIndex, newIndex });
    }
  };

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragEnd={handleDragEnd}
    >
      <SortableContext items={items} strategy={verticalListSortingStrategy}>
        <Collapse
          activeKey={activeKey}
          bordered={false}
          onChange={onToggle as ((key: string | string[]) => void) | undefined}
          className={classNames('sortable-collapse', className)}
        >
          {children}
        </Collapse>
      </SortableContext>
    </DndContext>
  );
};
